import _ from 'lodash'
import { generateArticleKey, InventoryArticle, needsGradeCompletion } from 'shared'

/**
 *
 * in the comment map I don't care about having a list of articles
 * because later I will need to group this list by locations,
 * so I declare only one InventoryArticle that will later have the list of locations
 * as if we were in the inventory with grouped locations
 *
 */
export interface InventoryCommentsMap {
  [key: string]: {
    article: InventoryArticle
    comments: string[]
  }
}

export const getCommentsMapForeachInventoryArticle = (
  inventoryArticles: InventoryArticle[]
): InventoryCommentsMap => {
  const commentsMap: InventoryCommentsMap = {}

  // I clone the inventory because otherwise I would have unwanted side effects
  const inventoryClone = _.cloneDeep(inventoryArticles)

  inventoryClone.forEach((article) => {
    const key = generateArticleKey(article)

    if (!commentsMap[key]) {
      // Let's create a new article to avoid side effects on the original structure
      const newArticle = article.copyWithNewId()

      // if the location is not already valued, we will value it with the comment found
      if (newArticle.locations![0].name === '')
        newArticle.locations = [{ ...newArticle.locations![0], name: newArticle.comment }]

      commentsMap[key] = {
        article: newArticle,
        comments: [article.comment],
      }
    } else {
      if (!commentsMap[key].comments.includes(article.comment)) {
        commentsMap[key].comments.push(article.comment)
      }

      // We add the new location based on the comment
      commentsMap[key].article.locations?.push({
        ...article.locations![0],
        name: article.locations![0].name !== '' ? article.locations![0].name : article.comment,
      })

      commentsMap[key].article.quantity += article.quantity

      // I want the comment to be the last one read by default
      commentsMap[key].article.comment = article.comment
    }
  })

  return commentsMap
}

/**
 * Whether the fetched stock still has a graded card waiting for its grade. Having graded cards is not enough:
 * a card whose comment already carries the grade needs nothing from the user, so gating the "check graded items"
 * step on that alone opened the step on a table with no rows to fill in.
 */
export const checkIfThereAreGradedCards = (fetchedStock: InventoryArticle[]) => {
  return fetchedStock.some((article) => needsGradeCompletion(article))
}
